/**
 * BaseImageryLayer — satellite basemap under the Google 3D Tiles.
 *
 * WHY (Brockmann 2026-08-31, searching "Strait of Hormuz"): the viewer shipped
 * with `scene.globe.show = false` and zero imagery layers, on the reasoning that
 * Google Photorealistic 3D Tiles "provide everything". They do NOT — that
 * coverage is METRO ONLY. Over open water, desert, border country or any rural
 * AOI there are no 3D tiles, so with the globe hidden the screen renders as a
 * flat void: no water, no coastline, no landmass.
 *
 * Observed directly at 26.3916N 56.2202E, alt 5138 m — the Strait of Hormuz —
 * a solid blue-grey field with no geography at all. At 10,000+ km it LOOKS like
 * an Earth because Google's coarse global tile fills the disc; zoom in over
 * water and that same tile stretches to flat colour. Ships, AIS tracks and
 * tactical overlays would float on nothing.
 *
 * FIX: turn the globe back on and drape satellite imagery from the server's
 * OWN /map-tiles route (?lyrs=s). No new vendor, no new key, no new egress
 * host — that route already exists, is proxied through api.atlasglinn.ai, and
 * returns image/jpeg 200s at every zoom tested. Where Google 3D Tiles DO have
 * coverage they render above this and nothing changes visually.
 */

import { createEffect, onCleanup } from "solid-js";
import { useCesium } from "../cesium/useCesium";
import { PROXY_BASE_URL } from "../config.ts";
export function BaseImagery() {
  const {
    viewer
  } = useCesium();
  createEffect(() => {
    const v = viewer();
    if (!v || v.isDestroyed()) return;
    let layer = null;
    try {
      // The globe was explicitly hidden at construction. Re-enable it: it is the
      // only surface that can carry imagery outside 3D-tile coverage.
      v.scene.globe.show = true;
      // Keep the atmosphere/fog OFF. CesiumProvider disables them deliberately —
      // b598/b600 both traced a render-context kill to ground-atmosphere shader
      // compilation on this iOS/WebKit build. Do not re-enable them here.
      v.scene.globe.showGroundAtmosphere = false;
      const provider = new Cesium.UrlTemplateImageryProvider({
        url: `${PROXY_BASE_URL}/map-tiles/{z}/{x}/{y}?lyrs=s`,
        maximumLevel: 20,
        credit: "Imagery ©Google"
      });
      layer = v.imageryLayers.addImageryProvider(provider);
      v.scene.requestRender();
      console.log("[BaseImagery] satellite basemap active (non-metro AOIs now render)");
    } catch (e) {
      console.error("[BaseImagery] failed:", e);
    }
    onCleanup(() => {
      const vv = viewer();
      if (!vv || vv.isDestroyed() || !layer) return;
      try {
        vv.imageryLayers.remove(layer, true);
        // Restore the shipped default so unmounting cannot leave the viewer in a
        // state CesiumProvider did not create.
        vv.scene.globe.show = false;
      } catch (_) {/* torn down */}
    });
  });
  return null;
}
export default BaseImagery;